import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ChevronLeft, Lock, Mail } from "lucide-react";
import { toast } from "sonner";
import { logWarn } from "@/lib/log";
import { API, setSession } from "@/lib/auth";

const BG_URL =
  "https://images.unsplash.com/photo-1554684726-0830be8cdbcd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMHdoaXRlJTIwYXJjaGl0ZWN0dXJlJTIwbWluaW1hbHxlbnwwfHx8fDE3Nzg1MzU0OTR8MA&ixlib=rb-4.1.0&q=85";

/**
 * Reusable login screen used by both /admin/login (expectedRole='admin')
 * and /instructor/login (expectedRole='instructor').
 */
export default function LoginScreen({
  expectedRole = "admin",
  title = "Iniciar sesión",
  badge = "Acceso Gestor",
  subtitle = "Introduce tus credenciales para administrar cursos, asistencias y preguntas.",
}) {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) {
      toast.error("Correo y contraseña son obligatorios.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, {
        username: username.trim(),
        password,
        remember,
      });
      const {
        token,
        username: returnedUsername,
        must_change_password,
      } = res.data;
      // Decode JWT payload to read role (without verification — just for UX routing)
      let role = "admin";
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        role = payload.role || "admin";
      } catch (decodeError) {
        // Malformed token payload — defaults to "admin"; auth still works
        // because the backend re-verifies the JWT on each request.
        logWarn("Could not decode JWT role", decodeError);
      }
      if (expectedRole && role !== expectedRole) {
        toast.error(
          expectedRole === "admin"
            ? "Esta cuenta no es de gestor. Usa el acceso de instructor."
            : "Esta cuenta no es de instructor. Usa el acceso de gestor."
        );
        setLoading(false);
        return;
      }
      setSession(token, returnedUsername, role, remember);
      // Si viene de una recuperación por email, tiene que cambiar la
      // contraseña antes de acceder al resto de la app.
      if (must_change_password) {
        toast.info("Debes elegir una contraseña nueva antes de continuar.");
        navigate(
          role === "admin"
            ? "/admin/cambiar-contrasena"
            : "/instructor/cambiar-contrasena"
        );
        return;
      }
      toast.success(`Bienvenido, ${returnedUsername}`);
      navigate(role === "instructor" ? "/instructor" : "/admin");
    } catch (err) {
      const msg =
        err.response?.data?.detail || "Error al iniciar sesión. Inténtalo de nuevo.";
      toast.error(typeof msg === "string" ? msg : "Error al iniciar sesión");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="relative min-h-screen w-full flex items-center justify-center px-6 py-12"
      data-testid={`login-screen-${expectedRole}`}
    >
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${BG_URL})` }}
        aria-hidden
      />
      <div className="absolute inset-0 bg-white/85 backdrop-blur-xl" aria-hidden />

      <div className="relative z-10 w-full max-w-md">
        <button
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-2 text-sm font-medium text-zinc-700 hover:text-zinc-950 transition-colors mb-6"
          data-testid="login-back-button"
        >
          <ChevronLeft className="h-4 w-4" />
          Volver al acceso
        </button>

        <div className="bg-white border border-zinc-200 rounded-lg shadow-[0_1px_0_rgba(0,0,0,0.04),0_24px_60px_-24px_rgba(0,0,0,0.18)] p-8 md:p-10">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-zinc-500 mb-8">
            <span className="inline-block h-2 w-2 bg-black" />
            {badge}
          </div>

          <h1 className="heading-font text-4xl sm:text-5xl font-extrabold text-zinc-950 leading-[0.95]">
            {title}
          </h1>
          <p className="mt-3 text-zinc-600 text-base">{subtitle}</p>

          <form onSubmit={handleSubmit} className="mt-10 space-y-5">
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Correo electrónico
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-400" />
                <Input
                  type="email"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="usuario@dominio.com"
                  className="touch-input pl-12 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
                  data-testid="login-username-input"
                  autoFocus
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Contraseña
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-400" />
                <Input
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="touch-input pl-12 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
                  data-testid="login-password-input"
                />
              </div>
            </div>

            <label
              className="flex cursor-pointer select-none items-center gap-3 rounded-md border-2 border-zinc-200 bg-zinc-50 px-4 py-3 text-sm font-medium text-zinc-700 transition-colors hover:border-zinc-300"
              data-testid="login-remember-label"
            >
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="h-5 w-5 cursor-pointer accent-zinc-950"
                data-testid="login-remember-checkbox"
              />
              <span>
                Mantener sesión iniciada
                <span className="block text-xs font-normal text-zinc-500">
                  No tendrás que volver a entrar durante un año, aunque cierres
                  el navegador.
                </span>
              </span>
            </label>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-14 text-base font-semibold bg-zinc-950 hover:bg-zinc-800 text-white rounded-md transition-colors mt-4"
              data-testid="login-submit-button"
            >
              {loading ? "Entrando…" : "Entrar"}
            </Button>

            {(expectedRole === "instructor" || expectedRole === "admin") && (
              <div className="pt-2 text-center">
                <button
                  type="button"
                  onClick={() =>
                    navigate(
                      expectedRole === "admin"
                        ? "/admin/recuperar-contrasena"
                        : "/instructor/recuperar-contrasena"
                    )
                  }
                  className="text-sm font-medium text-zinc-600 hover:text-zinc-950 underline underline-offset-4 transition-colors"
                  data-testid="login-forgot-password-link"
                >
                  ¿Has olvidado tu contraseña?
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
